import
{
  loginFailure,
  loginStart,
  loginSuccess,
  signUpStart,
  signUpSuccess,
  signUpFailure,
  otpVerifyStart,
  otpVerifySuccess,
  otpVerifyFailure,
  resendOTPStart,
  resendOTPSuccess,
  resendOTPFailure,
  forgotPasswordStart,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFailure,
  logout,
  updateUserProfile,
  setGoogleConnectionStatus,
} from '../slices/authSlice';
import { clearDashboard } from '../slices/dashboardSlice';
import { showError, showSuccess } from '../../utils/toast';
import { signIn as signInService, signUp as signUpService, otpVerification as otpVerificationService, resendOTP as resendOTPService, forgotPassword as forgotPasswordService, resetPassword as resetPasswordService, googleOAuthCallback as googleOAuthCallbackService, googleDisconnect, getCreatorProfile, getCurrentUser, deleteAccount as deleteAccountService } from '../../services/authService';
import { apiInvoker } from '../../lib/apiInvoker';
import { API_URLS } from '../../lib/apiUrls';
import { getErrorMessage, getStatusBasedErrorMessage } from '../../utils/errorHandler';

export const loginUser = (credentials, rememberMe = false, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(loginStart());
      const response = await signInService(credentials);
      
      if (response?.requiresVerification)
      {
        const email = response?.data?.email || credentials?.email;
        dispatch(loginFailure('Account not verified'));
        showSuccess('OTP sent to your email. Please verify your account.');
        if (navigation && email)
        {
          navigation(`/otp-verification?email=${encodeURIComponent(email)}&fromForgot=false`);
        }
        return response;
      }
      
      const token = response?.data?.token || response?.token;
      const user = response?.data?.user || null;
      dispatch(loginSuccess({ token, user, rememberMe }));
      showSuccess('Sign In successful!');
      navigation?.('/dashboard');
      return response;
    } catch (error)
    {
      const errorResponse = error?.response?.data || error?.data || {};
      
      if (errorResponse?.requiresVerification)
      {
        const email = errorResponse?.data?.email || credentials?.email;
        dispatch(loginFailure('Account not verified'));
        showSuccess('OTP sent to your email. Please verify your account.');
        if (navigation && email)
        {
          navigation(`/otp-verification?email=${encodeURIComponent(email)}&fromForgot=false`);
        }
        return error;
      }
      
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(loginFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const signUpUser = (credentials, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(signUpStart());
      const response = await signUpService(credentials);
      dispatch(signUpSuccess());
      showSuccess('Sign up successful');

      const email = credentials.email;
      if (navigation)
      {
        navigation(`/otp-verification?email=${ encodeURIComponent(email) }&fromForgot=false`);
      }

      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(signUpFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const verifyOTP = (otpData, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(otpVerifyStart());
      const response = await otpVerificationService(otpData);

      const token = response?.data?.token || response?.token;
      const user = response?.data?.user || response?.user || null;
      const { email, fromForgot } = otpData;
      if (fromForgot)
      {
        dispatch(otpVerifySuccess());
        showSuccess('OTP verified successfully!');
        if (navigation)
        {
          navigation(`/reset-password?email=${ encodeURIComponent(email) }`);
        }
      } else if (token && user)
      {
        dispatch(loginSuccess({ token, user, rememberMe: false }));
        dispatch(updateUserProfile(user));
        dispatch(otpVerifySuccess());
        showSuccess('Account created and verified successfully!');

        if (navigation)
        {
          navigation('/dashboard');
        }
      } else
      {
        dispatch(otpVerifySuccess());
        showSuccess('OTP verified successfully!');
        if (navigation)
        {
          navigation('/signin');
        }
      }

      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(otpVerifyFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const resendOTPCode = (email) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(resendOTPStart());
      const response = await resendOTPService({ email });
      dispatch(resendOTPSuccess());
      showSuccess('OTP resent successfully!');
      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(resendOTPFailure());
      showError(errorMessage);
      throw error;
    }
  };
};

export const forgotPassword = (email, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(forgotPasswordStart());
      const response = await forgotPasswordService({ email });
      dispatch(forgotPasswordSuccess());

      const message = response?.message || 'OTP sent to your email successfully!';
      showSuccess(message);

      if (navigation)
      {
        setTimeout(() =>
        {
          navigation(`/otp-verification?email=${ encodeURIComponent(email) }&fromForgot=true`);
        }, 1500);
      }

      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(forgotPasswordFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const resetPassword = (passwordData, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(resetPasswordStart());
      const response = await resetPasswordService({
        email: passwordData.email,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });
      dispatch(resetPasswordSuccess());

      showSuccess('Password reset successfully! Please sign in with your new password.');

      if (navigation)
      {
        navigation('/signin', { replace: true });
      }

      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(resetPasswordFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const logoutUser = (suppressToast = false) =>
{
  return (dispatch) =>
  {
    dispatch(clearDashboard());
    dispatch(logout());
    if (!suppressToast)
    {
      localStorage.setItem('showLogoutToast', 'true');
    }
  };
};

export const updateProfile = (profileData) =>
{
  return async (dispatch) =>
  {
    try
    {
      const payload = {};
      if (profileData.username)
      {
        payload.username = profileData.username;
      }
      if (profileData.currentPassword)
      {
        payload.currentPassword = profileData.currentPassword;
      }
      if (profileData.newPassword)
      {
        payload.newPassword = profileData.newPassword;
      }
      const url = API_URLS.USER.UPDATE_PROFILE();
      const response = await apiInvoker.put(url, payload);

      if (response?.success || response?.data || response)
      {
        if (profileData.username)
        {
          const updatedUser = response?.data?.user || response?.data?.profile || response?.user || response?.data || response;
          if (updatedUser && typeof updatedUser === 'object')
          {
            dispatch(updateUserProfile({
              ...updatedUser,
              fullName: profileData.username
            }));
          } else
          {
            dispatch(updateUserProfile({
              fullName: profileData.username
            }));
          }
        }

        showSuccess('Profile updated successfully!');
        return response;
      } else
      {
        throw new Error(response?.message || 'Failed to update profile');
      }
    } catch (error)
    {
      const errorMessage = getErrorMessage(error, 'Failed to update profile');
      showError(errorMessage);
      throw error;
    }
  };
};

export const googleConnect = () =>
{
  return async (dispatch) =>
  {
    try
    {
      const url = API_URLS.AUTH.GOOGLE_CONNECT;
      const response = await apiInvoker.get(url);
      return response;
    } catch (error)
    {
      console.error("Error connecting Google:", error);
      throw error;
    }
  };
};

export const googleConnectStatus = (forceRefresh = false) =>
{
  return async (dispatch, getState) =>
  {
    try
    {
      const state = getState();
      const cachedStatus = state.auth.googleConnectionStatus;
      
      if (!forceRefresh && cachedStatus !== null) {
        return { success: true, data: cachedStatus };
      }
      
      const url = API_URLS.AUTH.GOOGLE_CONNECT_STATUS;
      const response = await apiInvoker.get(url);
      
      if (response?.success && response?.data) {
        dispatch(setGoogleConnectionStatus(response.data));
      }
      
      return response;
    } catch (error)
    {
      console.error("Error checking Google connect status:", error);
      throw error;
    }
  };
};

export const googleOAuthCallback = (code, state, navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      dispatch(loginStart());
      const response = await googleOAuthCallbackService(code, state);
      const token = response?.data?.data?.token || response?.data?.token || response?.token;
      const user = response?.data?.data?.user || response?.data?.user || null;
      dispatch(loginSuccess({ token, user, rememberMe: true }));
      
      if (user && user.authProvider === 'google') {
        dispatch(setGoogleConnectionStatus({ 
          isConnected: true, 
          googleEmail: user.email || '' 
        }));
      }
      
      showSuccess('Sign In successful!');
      navigation?.('/dashboard');
      return response;
    } catch (error)
    {
      const errorMessage = getStatusBasedErrorMessage(error);
      dispatch(loginFailure(errorMessage));
      showError(errorMessage);
      throw error;
    }
  };
};

export const googleDisconnectAction = () =>
{
  return async (dispatch) =>
  {
    try
    {
      const response = await googleDisconnect();
      if (response?.success)
      {
        dispatch(setGoogleConnectionStatus({ isConnected: false, googleEmail: '' }));
        showSuccess('Google account disconnected successfully');
        return response;
      } else
      {
        throw new Error(response?.message || 'Failed to disconnect Google account');
      }
    } catch (error)
    {
      const errorMessage = getErrorMessage(error, 'Failed to disconnect Google account');
      showError(errorMessage);
      throw error;
    }
  };
};

export const fetchCreatorProfileAction = () =>
{
  return async (dispatch) =>
  {
    try
    {
      const response = await getCreatorProfile();
      return response;
    } catch (error)
    {
      console.error('Error fetching creator profile:', error);
      throw error;
    }
  };
};

export const fetchCurrentUser = () =>
{
  return async (dispatch) =>
  {
    try
    {
      const response = await getCurrentUser();
      const user = response?.data?.data?.user || response?.data?.user || response?.user || null;
      
      if (user)
      {
        dispatch(updateUserProfile(user));
        return { success: true, user };
      }
      return { success: false };
    } catch (error)
    {
      if (error?.status === 404) {
        return { success: false, error: null };
      }
      console.error('Error fetching current user:', error);
      return { success: false, error };
    }
  };
};

export const createTone = (creatorVideos) =>
{
  return async (dispatch) =>
  {
    try
    {
      const url = API_URLS.USER.CREATE_CREATOR_PROFILE();
      const payload = {
        creatorVideos: creatorVideos.map(url => ({ url }))
      };
      console.log(payload);
      
      // Creator analysis can take several minutes, set timeout to 10 minutes
      const config = {
        timeout: 600000 // 10 minutes
      };
      const response = await apiInvoker.post(url, payload, config);

      if (response?.success || response?.data || response)
      {
        showSuccess('Creator profile analyzed successfully!');
        return { success: true, data: response };
      } else
      {
        throw new Error(response?.message || 'Failed to analyze creator profile');
      }
    } catch (error)
    {
      const errorMessage = getErrorMessage(error, 'Failed to analyze creator profile');
      showError(errorMessage);
      const errorObj = new Error(errorMessage);
      errorObj.data = error?.data || error?.response?.data;
      throw errorObj;
    }
  };
};

export const deleteAccount = (navigation = null) =>
{
  return async (dispatch) =>
  {
    try
    {
      const response = await deleteAccountService();
      if (response?.success || response)
      {
        dispatch(logoutUser(true));
        showSuccess('Account deleted successfully');
        if (navigation)
        {
          navigation('/signin', { replace: true });
        }
        return response;
      } else
      {
        throw new Error(response?.message || 'Failed to delete account');
      }
    } catch (error)
    {
      const errorMessage = getErrorMessage(error, 'Failed to delete account');
      showError(errorMessage);
      throw error;
    }
  };
};